/**
 * Utilidad para generar archivos .ics (iCalendar)
 * Genera eventos de calendario compatibles con Google Calendar, Outlook, Apple Calendar, etc.
 */

/**
 * Escapa caracteres especiales para formato ICS
 * @param {string} text - Texto a escapar
 * @returns {string} - Texto escapado
 */
function escapeICSText(text) {
  if (!text) return '';
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

/**
 * Aplica line folding según RFC 5545 (máximo 75 caracteres por línea)
 * @param {string} line - Línea a plegar
 * @returns {string} - Línea con folding aplicado
 */
function foldLine(line) {
  const maxLength = 75;
  if (line.length <= maxLength) return line;
  
  let result = '';
  let currentLine = line;
  
  while (currentLine.length > maxLength) {
    // Primera línea completa, siguientes con espacio al inicio
    if (result === '') {
      result = currentLine.substring(0, maxLength);
      currentLine = currentLine.substring(maxLength);
    } else {
      result += '\r\n ' + currentLine.substring(0, maxLength - 1);
      currentLine = currentLine.substring(maxLength - 1);
    }
  }
  
  if (currentLine.length > 0) {
    if (result === '') {
      result = currentLine;
    } else {
      result += '\r\n ' + currentLine;
    }
  }
  
  return result;
}

/**
 * Formatea una fecha a formato ICS (YYYYMMDDTHHMMSSZ)
 * @param {Date} date - Fecha a formatear
 * @returns {string} - Fecha en formato ICS UTC
 */
function formatDateToICS(date) {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
}

/**
 * Genera un UID único para el evento
 * @returns {string} - UID único
 */
function generateUID() {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${random}@cambiaconhelix.cl`;
}

/**
 * Genera el contenido del archivo .ics para una reserva
 * @param {Object} bookingData - Datos de la reserva
 * @param {Object} bookingData.station - Datos de la estación
 * @param {string} bookingData.station.name - Nombre de la estación
 * @param {string} bookingData.station.address - Dirección de la estación
 * @param {Date|string} bookingData.date - Fecha y hora de la cita
 * @param {Object} bookingData.customer - Datos del cliente
 * @param {string} bookingData.customer.name - Nombre del cliente
 * @param {string} bookingData.customer.email - Email del cliente
 * @param {Object} bookingData.vehicle - Datos del vehículo
 * @param {string} bookingData.vehicle.patent - Patente del vehículo
 * @param {string} bookingData.vehicle.brand - Marca del vehículo
 * @param {string} bookingData.vehicle.model - Modelo del vehículo
 * @param {number} [durationMinutes=30] - Duración de la cita en minutos
 * @returns {string} - Contenido del archivo .ics
 */
export function generateICSContent(bookingData, durationMinutes = 45) {
  const { station, date, customer, vehicle } = bookingData;
  
  // Convertir fecha si es string
  const startDate = typeof date === 'string' ? new Date(date) : date;
  const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);
  
  // Formatear fechas
  const dtStart = formatDateToICS(startDate);
  const dtEnd = formatDateToICS(endDate);
  const dtStamp = formatDateToICS(new Date());
  
  // Link a Google Maps con la dirección de la estación
  const stationAddress = station?.address || '';
  const stationCommune = station?.commune?.name || station?.commune || '';
  const fullAddress = stationCommune ? `${stationAddress}, ${stationCommune}` : stationAddress;
  const googleMapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
  
  // Construir descripción según requerimiento
  const description = [
    `Servicio Cambia con Helix – Cambio de aceite Shell.`,
    ``,
    `Te recomendamos llegar 10 minutos antes del horario agendado.`,
    `El equipo te esperará hasta 15 minutos después de la hora programada.`,
    ``,
    `Vehículo: ${vehicle?.brand || ''} ${vehicle?.model || ''}`,
    `Patente: ${vehicle?.patent || 'N/A'}`,
    `Marca: ${vehicle?.brand || 'N/A'}`,
    `Año: ${vehicle?.year || 'N/A'}`,
    ``,
    `Gracias por preferirnos.`,
  ].join('\\n');
  
  // Construir ubicación con link a Google Maps
  const location = fullAddress || 'Estación Cambia con Helix';
  
  // Título del evento
  const summary = `Cambio de Aceite Shell - Cambia con Helix`;
  
  // Generar UID único
  const uid = generateUID();
  
  // Construir archivo ICS con line folding para líneas largas
  const icsLines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Cambia con Helix//Reserva de Cambio de Aceite//ES',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-TIMEZONE:America/Santiago',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    foldLine(`SUMMARY:${escapeICSText(summary)}`),
    foldLine(`DESCRIPTION:${description}`),
    foldLine(`LOCATION:${escapeICSText(location)}`),
    foldLine(`URL:${googleMapsLink}`),
    'STATUS:CONFIRMED',
    'BEGIN:VALARM',
    'TRIGGER:-PT60M',
    'ACTION:DISPLAY',
    foldLine('DESCRIPTION:Recordatorio: Tu cambio de aceite Shell es en 1 hora'),
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR'
  ];
  
  // Unir con CRLF y agregar línea final
  return icsLines.join('\r\n') + '\r\n';
}

/**
 * Descarga el archivo .ics
 * @param {Object} bookingData - Datos de la reserva
 * @param {string} [filename] - Nombre del archivo (opcional)
 */
export function downloadICSFile(bookingData, filename) {
  const icsContent = generateICSContent(bookingData);
  
  // Crear blob con el contenido
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  
  // Generar nombre de archivo
  const defaultFilename = `reserva-cambia-con-helix-${Date.now()}.ics`;
  const finalFilename = filename || defaultFilename;
  
  // Crear link de descarga
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = finalFilename;
  
  // Simular click para descargar
  document.body.appendChild(link);
  link.click();
  
  // Limpiar
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

/**
 * Constante para la key de localStorage
 */
export const BOOKING_DATA_KEY = 'cch_last_booking_data';

/**
 * Guarda los datos de la reserva en localStorage
 * @param {Object} bookingData - Datos de la reserva
 */
export function saveBookingDataToStorage(bookingData) {
  try {
    const dataToSave = {
      ...bookingData,
      // Convertir fecha a ISO string para serialización
      date: bookingData.date instanceof Date 
        ? bookingData.date.toISOString() 
        : bookingData.date,
      // Agregar timestamp para control de expiración
      savedAt: new Date().toISOString()
    };
    localStorage.setItem(BOOKING_DATA_KEY, JSON.stringify(dataToSave));
  } catch (error) {
    console.error('Error guardando datos de reserva en localStorage:', error);
  }
}

/**
 * Recupera los datos de la reserva desde localStorage
 * @param {number} [maxAgeMinutes=60] - Máximo tiempo en minutos para considerar los datos válidos
 * @returns {Object|null} - Datos de la reserva o null si no existen o expiraron
 */
export function getBookingDataFromStorage(maxAgeMinutes = 60) {
  try {
    const stored = localStorage.getItem(BOOKING_DATA_KEY);
    if (!stored) return null;
    
    const data = JSON.parse(stored);
    
    // Verificar expiración
    if (data.savedAt) {
      const savedAt = new Date(data.savedAt);
      const now = new Date();
      const ageMinutes = (now - savedAt) / (1000 * 60);
      
      if (ageMinutes > maxAgeMinutes) {
        // Datos expirados, limpiar
        clearBookingDataFromStorage();
        return null;
      }
    }
    
    // Convertir fecha de vuelta a Date object
    if (data.date) {
      data.date = new Date(data.date);
    }
    
    return data;
  } catch (error) {
    console.error('Error recuperando datos de reserva desde localStorage:', error);
    return null;
  }
}

/**
 * Limpia los datos de la reserva de localStorage
 */
export function clearBookingDataFromStorage() {
  try {
    localStorage.removeItem(BOOKING_DATA_KEY);
  } catch (error) {
    console.error('Error limpiando datos de reserva de localStorage:', error);
  }
}

export default {
  generateICSContent,
  downloadICSFile,
  saveBookingDataToStorage,
  getBookingDataFromStorage,
  clearBookingDataFromStorage,
  BOOKING_DATA_KEY
};
